import { IFamilySchema } from '@cloudike/web_photos/dist/types/intarfaces/IFamilySchema'
import { IMember } from '@cloudike/web_photos/dist/types/intarfaces/IMember'
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { setIsInviteMembersModalOpen } from 'features/common/model/slice/appSlice'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import _ from 'lodash'
import { familyApi } from 'shared/api/familyApi'
import { membersApi } from 'shared/api/membersApi'
import { userApi } from 'shared/api/userApi'
import { RootState } from 'shared/config/store'
import { getFamilySdk } from 'shared/sdk/family'
import { packagesApi } from 'shared/api/packagesApi'
import { feedbackApi } from 'shared/api/feedbackApi'

import { IPackage } from '../types/packages'

export enum SortColumns {
  NAME = 'name',
  MODIFIED = 'updated',
  SIZE = 'file_info.size',
}

export enum OrderType {
  ASK = "asc",
  DESK = "desc"
}

interface State {
  familyData: IFamilySchema,
  sort: { by: SortColumns, direction: OrderType },
  packages: IPackage[];
  defaultPackage: IPackage | null;
}

export const fetchPackagesThunk = createAsyncThunk(
  'team/fetchPackagesThunk',
  async function (_, { getState, dispatch }) {
    const state = getState() as RootState
    const userID = state.user.user.id

    const response = await packagesApi.getPackages(userID) as any

    const packages = response?._embedded?.packages as IPackage[]

    if (!packages?.some(pkg => pkg.subscribed)) {
      const ordersResponse = await packagesApi.getOrders(userID) as any

      const orders = ordersResponse?.content
      const defaultPackage = orders?.find(order => order.created_by === 'dam_default')

      dispatch(teamActions.setDefaultPackage(defaultPackage))
    }

    return response?._embedded?.packages || [] as IPackage[]
  }
)

export const checkoutSessionThunk = createAsyncThunk(
  'team/checkoutSessionThunk',
  async function ({ packageID, callback, errorCallback }: { packageID: string, callback: (url: string) => void, errorCallback: (error: any) => void }, { getState }) {
    const state = getState() as RootState
    const userID = state.user.user.id
    try {
      const response = await packagesApi.checkoutSession(userID, packageID)

      callback(response.url)
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Something Went Wrong',
        message: 'Please try again',
      })
      console.error(error)
      errorCallback(error)
    }
  }
)

export const fetchCustomerPortalLinkThunk = createAsyncThunk(
  'team/fetchCustomerPortalLinkThunk',
  async function ({ callback, errorCallback }: { callback: (url: string) => void, errorCallback: (error: any) => void }, { getState }) {
    const state = getState() as RootState
    const userID = state.user.user.id
    try {
      const response = await packagesApi.getCustomerPortalLink(userID)

      callback(response.url)
    } catch (error) {
      console.error(error)
      errorCallback(error)
    }
  }
)

export const fetchFamilyThunk = createAsyncThunk(
  'family/fetchFamilyThunk',
  async function () {
    const sdk = getFamilySdk()
    const familyDataResponse = await sdk.getUserFamilies({ limit: 1 })
    const familyData = familyDataResponse.data._embedded.families[0]
    const familyMembers = await (await sdk.getFamilyMembers(familyData.id, { limit: 1000 })).data._embedded.members
    return {
      familyData,
      familyMembers: familyMembers
    }
  }
)

// export const checkTimelineItemsCountThunk = createAsyncThunk(
//   'family/checkTimelineItems',
//   async function (_, { dispatch }) {
//     const timelineSdk = getTimelineSdkByType(SDK_TYPES.DEFAULT)

//     const timelineItemsResponse = await timelineSdk.getTimelineItems({ offset: 0, limit: 1, total_count: true })
//     const userHasTimelineItems = Number(timelineItemsResponse.headers[TOTAL_COUNT_HEADER]) > 0

//     dispatch(actions.setUserHasTimelineItems(userHasTimelineItems))
//   }
// )

export const deleteFamilyMembersThunk = createAsyncThunk(
  'family/deleteFamilyMembersThunk',
  async function (members: IMember[], { getState, dispatch }) {
    try {
      const sdk = getFamilySdk()
      const state = getState() as RootState
      const familyId = state.family.familyData.id
      

      for (const member of members) {
        await sdk.deleteFamilyMember(familyId, member.id)
        dispatch(teamActions.deleteMember(member))
      }

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'All set!',
        message: 'The member was deleted',
      })

    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Hmm, something went wrong',
        message: 'Couldn’t delete the member',
      })
    }
  }
)

export const updateFamilyMemberRoleThunk = createAsyncThunk(
  'family/updateFamilyMemberRoleThunk',
  async function ({ member, role }: { member: IMember, role: string }, { getState, dispatch }) {
    try {
      const state = getState() as RootState
      const familyId = state.family.familyData.id

      await membersApi.updateUserRole(familyId, member.id, { role })
      
      dispatch(teamActions.updateMemberRole({ memberId: member.id, role }))

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Role updated',
        message: 'Member role updated successfully',
      })

    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Role update failed',
        message: 'Failed to update member role',
      })
    }
  }
)

export const updateFamilyNameThunk = createAsyncThunk(
  'family/updateFamilyNameThunk',
  async function ({ name }: { name: string }, { getState, dispatch }) {
    try {
      const state = getState() as RootState
      const familyId = state.family.familyData.id

      const response = await familyApi.updateFamilyName(familyId, { name })
      
      dispatch(teamActions.updateFamilyName({ name }))

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Team name updated',
        message: 'Team name updated successfully',
      })

      return response
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Failed to update team name',
        message: 'Failed to update team name. Please try again.',
      })
    }
  }
)

// export const deleteFamilyLinkThunk = createAsyncThunk(
//   'family/deleteFamilyLinkThunk',
//   async function (_, { getState }) {
//     try {
//       const sdk = getFamilySdk()
//       const state = getState() as RootState

//       const familyId = state.family.familyData.id

//       showGlobalProgressLoader()

//       const response = await sdk.lockUserFamily(familyId)

//       hideGlobalProgressLoader()
//       return response.data
//     } catch (error) {
//       showNotification({
//         type: NOTIFICATION_TYPES.WARNING,
//         ...getErrorData(error)
//       })
//     } finally {
//       hideGlobalProgressLoader()
//     }
//   }
// )

// export const createFamilyLinkThunk = createAsyncThunk(
//   'family/createFamilyLinkThunk',
//   async function (_, { getState }) {
//     try {
//       const sdk = getFamilySdk()
//       const state = getState() as RootState

//       const familyId = state.family.familyData.id

//       showGlobalProgressLoader()

//       const response = await sdk.unlockUserFamily(familyId)

//       hideGlobalProgressLoader()
//       return response.data
//     } catch (error) {
//       showNotification({
//         type: NOTIFICATION_TYPES.WARNING,
//         ...getErrorData(error)
//       })
//     } finally {
//       hideGlobalProgressLoader()
//     }
//   }
// )

export const deleteFamilyCloudThunk = createAsyncThunk(
  'family/deleteFamilyCloudThunk',
  async function (callback: () => void, { getState }) {
    try {
      const sdk = getFamilySdk()
      const state = getState() as RootState
      const familyId = state.family.familyData.id
      await sdk.deleteUserFamily(familyId)
      callback()
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Failed to delete family',
        message: error.message
      })
    } finally {
    }
  }
)

export const createFamilyCloudThunk = createAsyncThunk(
  'family/createFamilyCloudThunk',
  async function ({ data, callback }: {data: {name: string}, callback?: (familyUserId: number, ownerId: number) => void} , { dispatch }) {
    try {
      const sdk = getFamilySdk()

      const response = await sdk.createUserFamily(data)
      const familyData = response.data

      dispatch(teamActions.setFamilyData(response.data))

      if (callback) {
        callback(familyData.shared_user_id, familyData.owner_id)
      }
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Team created!',
        message: 'Your team has been created successfully',
      })
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Oops! Something Went Wrong',
        message: 'We couldn’t create the team. Try again, please!',
      })
    }
  }
)

export const leaveFromFamilyCloudThunk = createAsyncThunk(
  'family/leaveFromFamilyCloudThunk',
  async function ({ userId, callback }: { callback: () => void, userId: string }, { getState }) {
    try {
      const sdk = getFamilySdk()
      const state = getState() as RootState
      const familyId = state.family.familyData.id
      await sdk.deleteFamilyMember(familyId, userId)
      callback()
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Oops! Failed to Leave Family',
        message: 'Something went wrong while trying to leave the family. Please try again.'
      })
    } finally {
    }
  }
)

export const joinToFamilyCloudThunk = createAsyncThunk(
  'family/joinToFamilyCloudThunk',
  async function ({ fchash, successCallback, errorCallback }: { successCallback: (id: number, ownerId: number) => void, errorCallback: (error: any) => void, fchash: string }) {
    try {
      const sdk = getFamilySdk()

      await sdk.joinFamilyMember(fchash, {})
      const familyDataResponse = await sdk.getUserFamilies({ limit: 1 })

      const familyData = familyDataResponse.data._embedded.families[0]

      successCallback(familyData?.shared_user_id, familyData?.owner_id)
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Joined the Team!',
        message: 'You’ve successfully joined the team.',
      })
    } catch (error) {
      errorCallback(error.cause)
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Oops! Couldn’t Join the Team',
        message: 'Something went wrong while joining the team. Please try again.',
      })
    } finally {
    }
  }
)

export const sendInvitesToFamilyThunk = createAsyncThunk(
  'family/sendInvitesToFamilyThunk',
  async function ({ to }: {to: string[]}, { getState, dispatch }) {

    const state = getState() as RootState
    const userId = state.user.user.id
    const familyData = state.family.familyData
    try {
      if (to.length) {
        await Promise.allSettled(to.map(email => userApi.sendInviteToFamily(userId, familyData.id, email)))
      }
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Invite Sent!',
        message: 'Invite successfully sent',
      })
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Oops! Invite Failed',
        message: 'We couldn’t send the invite. Please try again.',
      })
    }
    finally {
      dispatch(setIsInviteMembersModalOpen(false))
    }
  }
)

// export const updateFamilyMemberThunk = createAsyncThunk(
//   'family/updateFamilyMemberThunk',
//   async function ({ member, data  }: { member: IMember, data: Partial<IMember> }, { dispatch, getState }) {
//     try {
//       const sdk = getFamilySdk()
//       const state = getState() as RootState
//       const familyId = state.family.familyData.id
//       const notificationTitle = t('l_notification_familyMemberRenamed')
//       showGlobalProgressLoader()

//       const { data:newData } = await sdk.updateFamilyMember(familyId, member.id, data)
//       dispatch(actions.updateFamilyMember({ member, data: newData }))

//       showNotification({
//         type: NOTIFICATION_TYPES.SUCCESS,
//         title: notificationTitle
//       })
//     } catch (error) {
//       showNotification({
//         type: NOTIFICATION_TYPES.WARNING,
//         ...getErrorData(error)
//       })
//     } finally {
//       hideGlobalProgressLoader()
//     }
//   }
// )
export const sendFeedbackThunk = createAsyncThunk(
  'family/sendFeedbackThunk',
  async function ({ name, email, message, successCallback, errorCallback }: { name: string, email: string, message?: string, successCallback?: () => void, errorCallback?: (error: any) => void }) {
    try {
      await feedbackApi.sendFeedback({
        name,
        email,
        comment: message,
      })

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Thank you!',
        message: 'Your submission has been received'
      })

      successCallback?.()
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Failed to send feedback',
        message: 'Failed to send feedback. Please try again.',
      })

      errorCallback?.(error)
    }
  }
)

const adapter = createEntityAdapter<IMember>()

export const teamMemberSelectors = adapter.getSelectors()


const initialState = adapter.getInitialState({
  familyData: {},
  sort: { by: SortColumns.NAME, direction: OrderType.ASK },
  packages: [],
  defaultPackage: null,
} as State)

const sortNodes = (nodes: any[], { by, direction }: any) => {
  const orderByType = (entity: { type: any }) => entity.type

  return _.chain(nodes)
    .orderBy(by === 'name' ? node => node.name.toUpperCase() : by, [direction])
    .orderBy(orderByType)
    .value()
}

export const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    setFamilyData: (state, action) => {
      state.familyData = action.payload
    },
    // updateFamilyData: (state, action) => {
    //   state.familyData = { ...state.familyData, ...action.payload }
    // },
    deleteMember: (state, action) => {
      adapter.removeOne(state, action.payload.id)
    },
    deleteMemberById: (state, action) => {
      adapter.removeOne(state, action.payload)
    },
    addMember: (state, action) => {
      const { member } = action.payload
      adapter.addOne(state, { ...member, isSelectable: true })
    },
    setAllMembers: (state, action) => {
      adapter.setAll(state, action.payload)
    },
    setPackages: (state, action) => {
      state.packages = action.payload
    },
    setDefaultPackage: (state, action) => {
      state.defaultPackage = action.payload
    },
    // toggleFamilyinviteModal: (state, action) => {
    //   state.isFamilyInviteModalOpened = action.payload
    // },
    // resetState: (state) => {
    //   state.familyData = {} as IFamilySchema
    //   adapter.removeAll(state)
    // },
    // setUserHasTimelineItems: (state, action) => {
    //   state.userHasTimelineItems = action.payload
    // },
    // setIsOnboardingActive: (state, action) => {
    //   state.isOnboardingActive = action.payload
    // },
    // setOnboardingStep: (state, action) => {
    //   state.onboardingStep = action.payload
    // },
    // updateFamilyMember: (state, action) => {
    //   adapter.updateOne(state, {
    //     id: action.payload.member.id,
    //     changes: action.payload.data
    //   })
    // },
    // selectItem: selectMembersListItemReducer(familyMemberSelectors),
    // selectItemWithPressedCtrl: selectMembersListItemWithPressedCtrlReducer(familyMemberSelectors),
    // selectItemWithPressedShift: selectMembersListItemWithPressedShiftReducer(familyMemberSelectors),
    // selectAll: selectAllMembersListItemsReducer,
    // unselectAll: unselectAllMembersListItemsReducer,
    updateMemberRole: (state, action) => {
      adapter.updateOne(state, {
        id: action.payload.memberId,
        changes: { role: action.payload.role }
      })
    },
    updateFamilyName: (state, action) => {
      state.familyData = { ...state.familyData, name: action.payload.name }
    },
  },
  extraReducers(builder) {
     
    builder
      .addCase(fetchFamilyThunk.fulfilled, (state, action) => {
        state.familyData = action.payload.familyData
        const members = sortNodes(action.payload.familyMembers, state.sort)
        adapter.setAll(state, members)
      })
      .addCase(fetchPackagesThunk.fulfilled, (state, action) => {
        state.packages = action.payload
      })
  },
})

export const teamActions = teamSlice.actions

export default teamSlice.reducer 
